import React, { useState, lazy, Suspense } from 'react';
import { Phone, Mail, MapPin, Clock, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FOOTER_LINKS, SOCIAL_LINKS, CONTACT_INFO, LEGAL_CONTENT } from '../constants/footerData';

const LegalModal = lazy(() => import('./LegalModal'));

export default function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'cookies' | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    quick: false,
    core: false,
    blog: false,
    cities: false,
    compliance: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const isHash = href.includes('#');
    
    if (isHash) {
      const isExternalHash = href.startsWith('/') && href.includes('#');
      const targetPath = isExternalHash ? href.split('#')[0] : '/';
      
      if (location.pathname !== targetPath) {
        if (isExternalHash) {
          // Already starts with /
        } else {
          e.preventDefault();
          navigate(href.startsWith('/') ? href : `/${href.replace('#', '/#')}`);
        }
      } else {
        e.preventDefault();
        const hash = href.includes('#') ? '#' + href.split('#')[1] : href;
        const targetId = hash.substring(1);
        const element = targetId ? document.getElementById(targetId) : null;
        
        if (hash === '#') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }
    } else if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Helper to find specific link from businessSetupLinks
  const findLink = (href: string) => {
    return FOOTER_LINKS.businessSetupLinks.find(link => link.href === href);
  };

  // Dynamically group links to maintain maximum compatibility & SEO integrity
  const coreServices = [
    findLink('/company-setup-saudi-arabia'),
    findLink('/commercial-registration-saudi-arabia'),
    findLink('/foreign-investor-setup'),
    findLink('/misa-license-saudi-arabia'),
    findLink('/llc-setup-saudi-arabia'),
  ].filter(Boolean) as { name: string, href: string }[];

  const cityServices = [
    findLink('/business-setup-riyadh'),
    findLink('/business-setup-jeddah'),
    findLink('/business-setup-dammam'),
    findLink('/business-setup-khobar'),
  ].filter(Boolean) as { name: string, href: string }[];

  // Remaining compliance, licenses & municipal services
  const excludedHrefs = [
    '/company-setup-saudi-arabia',
    '/commercial-registration-saudi-arabia',
    '/foreign-investor-setup',
    '/misa-license-saudi-arabia',
    '/llc-setup-saudi-arabia',
    '/business-setup-riyadh',
    '/business-setup-jeddah',
    '/business-setup-dammam',
    '/business-setup-khobar'
  ];

  const complianceServices = FOOTER_LINKS.businessSetupLinks.filter(
    link => !excludedHrefs.includes(link.href)
  );

  // Core guides/articles to display (top 5 from blogLinks)
  const coreGuides = (FOOTER_LINKS as any).blogLinks?.slice(0, 5) || [];

  return (
    <footer id="contact" className="relative bg-slate-50 text-brand-dark border-b border-slate-200 py-16 lg:py-24 overflow-hidden scroll-mt-20" dir="rtl">
      {/* Background decoration - Image Background */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none overflow-hidden grayscale mix-blend-overlay">
        <img 
          src="https://images.unsplash.com/photo-1550745165-9bc0b25272a7?q=20&w=1280&auto=format&fit=crop"
          alt=""
          width={1280}
          height={400}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-slate-50/90" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer Top Grid (5 Balanced Columns) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 xl:gap-12 mb-16">
          {/* Column 1: Logo & About (lg:col-span-3) */}
          <div className="lg:col-span-3 space-y-6 text-right">
            <img 
              src="https://blogger.googleusercontent.com/img/a/AVvXsEgk90zcNlep_PggBeutpAelZBCPIxHzO2NSOzQe9Mps-_8DjjQ2Uka89cam_ZKDmP_tv9UPcdrEu3KrcJtp5VFczXJhNruom741Knt2dtbFaAq1_-EAd8oTZqUgKSBa3ORoU3mvBT6ECZO7OiZbRcQmEcoS05PBRTMH1EZ7_eh7vkdR3HT1rmRnQlqOmiqx" 
              alt="الحلول الرقمية لريادة الأعمال" 
              width={160}
              height={64}
              className="h-14 lg:h-16 w-auto brightness-110 ml-auto"
              loading="lazy"
              decoding="async"
              referrerPolicy="strict-origin-when-cross-origin"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
            <p className="text-slate-600 leading-relaxed text-sm lg:text-[15px]">
              نقدم خدمات تأسيس الشركات في السعودية بسرعة واحترافية، تشمل إصدار السجل التجاري، التراخيص، وتأسيس المؤسسات والشركات المحلية والأجنبية مع ضمان الامتثال الكامل للأنظمة. حلول متكاملة لرواد الأعمال والمستثمرين لبدء أعمالهم بسهولة وثقة.
            </p>
            <div className="flex justify-start gap-3">
              {SOCIAL_LINKS.map(({ Icon, title, url }, i) => (
                <a 
                  key={i} 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title={title} 
                  aria-label={`زيارة صفحتنا على ${title}`}
                  className="bg-slate-100 p-2.5 rounded-full hover:bg-brand-accent hover:text-brand-dark transition-all duration-300 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links (lg:col-span-2) */}
          <div className="lg:col-span-2 text-right">
            {/* Header / Toggle button on Mobile */}
            <button
              onClick={() => toggleSection('quick')}
              className="w-full lg:pointer-events-none flex items-center justify-between text-right py-3 lg:py-0 border-b border-slate-200/60 lg:border-none mb-4 lg:mb-8"
            >
              <h3 className="text-base lg:text-[17px] font-bold text-brand-dark relative inline-block pb-1 lg:pb-3 lg:after:content-[''] lg:after:absolute lg:after:bottom-0 lg:after:right-0 lg:after:w-12 lg:after:h-1 lg:after:bg-brand-accent lg:after:rounded-full">
                روابط سريعة
              </h3>
              <ChevronDown 
                size={18} 
                className={`lg:hidden text-slate-400 transform transition-transform duration-300 ${openSections.quick ? 'rotate-180' : ''}`} 
              />
            </button>
            {/* Links List */}
            <div className={`mt-2 lg:mt-0 lg:block ${openSections.quick ? 'block' : 'hidden'}`}>
              <ul className="space-y-3 pb-4 lg:pb-0">
                {FOOTER_LINKS.quickLinks.map((item) => (
                  <li key={item.name}>
                    <a 
                      href={item.href} 
                      onClick={(e) => handleLinkClick(e, item.href)}
                      aria-label={`انتقل إلى ${item.name}`}
                      className="text-slate-600 hover:text-brand-accent hover:-translate-x-2 inline-block transition-all duration-300 text-sm lg:text-base font-medium"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 3: Core Services (lg:col-span-2) */}
          <div className="lg:col-span-2 text-right">
            <button
              onClick={() => toggleSection('core')}
              className="w-full lg:pointer-events-none flex items-center justify-between text-right py-3 lg:py-0 border-b border-slate-200/60 lg:border-none mb-4 lg:mb-8"
            >
              <h3 className="text-base lg:text-[17px] font-bold text-brand-dark relative inline-block pb-1 lg:pb-3 lg:after:content-[''] lg:after:absolute lg:after:bottom-0 lg:after:right-0 lg:after:w-12 lg:after:h-1 lg:after:bg-brand-accent lg:after:rounded-full">
                الخدمات الأساسية
              </h3>
              <ChevronDown 
                size={18} 
                className={`lg:hidden text-slate-400 transform transition-transform duration-300 ${openSections.core ? 'rotate-180' : ''}`} 
              />
            </button>
            <div className={`mt-2 lg:mt-0 lg:block ${openSections.core ? 'block' : 'hidden'}`}>
              <ul className="space-y-3 pb-4 lg:pb-0">
                {coreServices.map((item) => (
                  <li key={item.name}>
                    <a 
                      href={item.href} 
                      onClick={(e) => handleLinkClick(e, item.href)}
                      aria-label={`انتقل إلى ${item.name}`}
                      className="text-slate-600 hover:text-brand-accent hover:-translate-x-2 inline-block transition-all duration-300 text-sm lg:text-base font-medium"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
                <li>
                  <a 
                    href="/services" 
                    onClick={(e) => handleLinkClick(e, '/services')}
                    className="text-brand-accent hover:text-brand-accent/80 hover:-translate-x-2 inline-block transition-all duration-300 text-sm lg:text-base font-bold"
                  >
                    عرض جميع الخدمات ←
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Column 4: Guides & Articles (lg:col-span-2) */}
          <div className="lg:col-span-2 text-right">
            <button
              onClick={() => toggleSection('blog')}
              className="w-full lg:pointer-events-none flex items-center justify-between text-right py-3 lg:py-0 border-b border-slate-200/60 lg:border-none mb-4 lg:mb-8"
            >
              <h3 className="text-base lg:text-[17px] font-bold text-brand-dark relative inline-block pb-1 lg:pb-3 lg:after:content-[''] lg:after:absolute lg:after:bottom-0 lg:after:right-0 lg:after:w-12 lg:after:h-1 lg:after:bg-brand-accent lg:after:rounded-full">
                أدلة شاملة ومقالات
              </h3>
              <ChevronDown 
                size={18} 
                className={`lg:hidden text-slate-400 transform transition-transform duration-300 ${openSections.blog ? 'rotate-180' : ''}`} 
              />
            </button>
            <div className={`mt-2 lg:mt-0 lg:block ${openSections.blog ? 'block' : 'hidden'}`}>
              <ul className="space-y-3 pb-4 lg:pb-0">
                {coreGuides.map((item: any) => (
                  <li key={item.name}>
                    <a 
                      href={item.href} 
                      onClick={(e) => handleLinkClick(e, item.href)}
                      aria-label={`انتقل إلى ${item.name}`}
                      className="text-slate-600 hover:text-brand-accent hover:-translate-x-2 inline-block transition-all duration-300 text-sm lg:text-base font-medium line-clamp-2"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 5: Contact Info (lg:col-span-3) */}
          <div className="lg:col-span-3 text-right">
            <h3 className="text-base lg:text-[17px] font-bold text-brand-dark mb-6 lg:mb-8 relative inline-block pb-3 after:content-[''] after:absolute after:bottom-0 after:right-0 after:w-12 after:h-1 after:bg-brand-accent after:rounded-full">
              تواصل معنا
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="bg-slate-100 p-2 rounded-xl text-brand-accent shrink-0">
                  <Phone size={16} />
                </div>
                <div>
                  <p className="font-bold text-xs text-brand-dark mb-0.5">الهاتف</p>
                  <a 
                    href={`tel:${CONTACT_INFO.phone}`} 
                    aria-label={`الاتصال بنا هاتفياً على الرقم ${CONTACT_INFO.phone}`}
                    className="text-slate-600 hover:text-brand-accent transition-colors text-[13px] md:text-sm font-medium"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-slate-100 p-2 rounded-xl text-brand-accent shrink-0">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="font-bold text-xs text-brand-dark mb-0.5">البريد الإلكتروني</p>
                  <a 
                    href={`mailto:${CONTACT_INFO.email}`} 
                    aria-label="مراسلتنا عبر البريد الإلكتروني"
                    className="text-slate-600 hover:text-brand-accent transition-colors text-[13px] md:text-sm font-medium break-all"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-slate-100 p-2 rounded-xl text-brand-accent shrink-0">
                  <MapPin size={16} />
                </div>
                <div>
                  <p className="font-bold text-xs text-brand-dark mb-0.5">العنوان</p>
                  <span className="text-slate-600 text-[13px] md:text-sm leading-relaxed font-medium">{CONTACT_INFO.address}</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-slate-100 p-2 rounded-xl text-brand-accent shrink-0">
                  <Clock size={16} />
                </div>
                <div>
                  <p className="font-bold text-xs text-brand-dark mb-0.5">ساعات العمل (السبت - الخميس)</p>
                  <p className="text-slate-600 text-xs mt-0.5">الصباح: 8:00 ص - 12:00 م</p>
                  <p className="text-slate-600 text-xs mt-0.5">المساء: 4:00 م - 12:00 ص</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Directory Sitemap Section (Cities & Support Compliance Links) */}
        <div className="border-t border-slate-200 pt-10 mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Subsection 1: Cities Setup (lg:col-span-4) */}
            <div className="lg:col-span-4 text-right">
              <button
                onClick={() => toggleSection('cities')}
                className="w-full lg:pointer-events-none flex items-center justify-between text-right py-2 lg:py-0 border-b border-slate-100 lg:border-none mb-3 lg:mb-5"
              >
                <h4 className="text-sm lg:text-base font-bold text-brand-dark">
                  تأسيس الشركات حسب المدن
                </h4>
                <ChevronDown 
                  size={16} 
                  className={`lg:hidden text-slate-400 transform transition-transform duration-300 ${openSections.cities ? 'rotate-180' : ''}`} 
                />
              </button>
              <div className={`mt-2 lg:mt-0 lg:block ${openSections.cities ? 'block' : 'hidden'}`}>
                <div className="flex flex-wrap gap-2 pb-4 lg:pb-0">
                  {cityServices.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => handleLinkClick(e, item.href)}
                      className="bg-slate-100 hover:bg-brand-accent hover:text-brand-dark px-3 py-1.5 rounded-lg text-slate-600 text-xs font-medium transition-all duration-300 border border-slate-200/50"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Subsection 2: Government & Compliance Support Services (lg:col-span-8) */}
            <div className="lg:col-span-8 text-right">
              <button
                onClick={() => toggleSection('compliance')}
                className="w-full lg:pointer-events-none flex items-center justify-between text-right py-2 lg:py-0 border-b border-slate-100 lg:border-none mb-3 lg:mb-5"
              >
                <h4 className="text-sm lg:text-base font-bold text-brand-dark">
                  خدمات التراخيص، الامتثال والخدمات المساندة
                </h4>
                <ChevronDown 
                  size={16} 
                  className={`lg:hidden text-slate-400 transform transition-transform duration-300 ${openSections.compliance ? 'rotate-180' : ''}`} 
                />
              </button>
              <div className={`mt-2 lg:mt-0 lg:block ${openSections.compliance ? 'block' : 'hidden'}`}>
                <div className="flex flex-wrap gap-2 pb-4 lg:pb-0">
                  {complianceServices.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => handleLinkClick(e, item.href)}
                      className="bg-slate-100 hover:bg-brand-accent hover:text-brand-dark px-3 py-1.5 rounded-lg text-slate-600 text-xs font-medium transition-all duration-300 border border-slate-200/50"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright & Legal Links */}
        <div className="border-t border-slate-200 pt-8 mt-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-right">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12 w-full md:w-auto">
            <p className="text-slate-400 text-xs lg:text-sm">
              جميع الحقوق محفوظة لدى الحلول الرقمية لريادة الأعمال - {new Date().getFullYear()}
            </p>
          </div>

          <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-3 text-slate-500 text-xs w-full md:w-auto">
            <Link 
              to="/privacy-policy" 
              aria-label="عرض سياسة الخصوصية"
              className="hover:text-brand-accent transition-colors cursor-pointer font-medium"
            >
              سياسة الخصوصية
            </Link>
            <Link 
              to="/terms" 
              aria-label="عرض الشروط والأحكام"
              className="hover:text-brand-accent transition-colors cursor-pointer font-medium"
            >
              الشروط والأحكام
            </Link>
            <button 
              onClick={() => setActiveModal('cookies')} 
              aria-label="عرض سياسة ملفات تعريف الارتباط"
              className="hover:text-brand-accent transition-colors cursor-pointer font-medium"
            >
              ملفات تعريف الارتباط
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {activeModal && (
          <Suspense fallback={null}>
            <LegalModal 
              key={activeModal}
              onClose={() => setActiveModal(null)} 
              title={
                activeModal === 'privacy' ? "سياسة الخصوصية" : 
                activeModal === 'terms' ? "الشروط والأحكام" : 
                "سياسة ملفات تعريف الارتباط"
              } 
              content={LEGAL_CONTENT[activeModal]} 
            />
          </Suspense>
        )}
      </AnimatePresence>
    </footer>
  );
}
